import { setDefaultOptions } from 'date-fns';
import { Tooltip } from 'radix-ui';
import { lazy, StrictMode, Suspense, useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { acceptConsentWithoutBanner } from 'views/consentBanner/consent-utils';
import GlobalSnackBar from 'views/global/SnackBar/GlobalSnackBar';

export type AppContainerType =
  | 'container'
  | 'container-login'
  | 'container-external'
  | 'container-shared'
  | 'module-container';

const UnAuthorizedContainerAppLazy = lazy(
  () => import(/* webpackChunkName : "login-container" */ 'views/UnAuthorizedContainerApp')
);

const AuthorizedContainerAppLazy = lazy(
  () => import(/* webpackChunkName : "main-container" */ 'views/AuthorizedContainerApp')
);

const ModuleContainerAppLazy = lazy(
  () => import(/* webpackChunkName : "module-container" */ 'views/ModuleContainerApp')
);

let root: Root | null = null;

export function renderRootApp(container: AppContainerType, { to = '' }: { to?: string }) {
  const appRoot = document.getElementById('fileee-app');

  if (container === 'container-external' || container === 'module-container') {
    acceptConsentWithoutBanner();
  }

  if (!root) {
    root = createRoot(appRoot);
  }

  root.render(
    <StrictMode>
      <Suspense fallback={null}>
        <Tooltip.Provider>
          <RootApp key={container} container={container} to={to} />
        </Tooltip.Provider>
        <GlobalSnackBar />
      </Suspense>
    </StrictMode>
  );
}

function RootApp({ container, to = '' }: { container: AppContainerType; to?: string }) {
  useEffect(() => {
    setDefaultOptions({ weekStartsOn: 1 });
  }, []);

  switch (container) {
    case 'container': {
      return <AuthorizedContainerAppLazy navigateTo={to} />;
    }
    case 'container-external': {
      return <UnAuthorizedContainerAppLazy handleDocumentRoute={true} />;
    }
    case 'module-container': {
      return <ModuleContainerAppLazy navigateTo={to} />;
    }
    default: {
      return <UnAuthorizedContainerAppLazy handleDocumentRoute={false} />;
    }
  }
}
